import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth-store";
import { useAlertEvents, useAcknowledgeAlert } from "@/hooks/use-alerts";
import { cn } from "@/lib/utils";
import { Bell, Search, User, LogOut, Settings, ChevronRight, Check, X } from "lucide-react";

export function Header() {
  const navigate = useNavigate();
  const { user, tenant, logout } = useAuthStore();
  const [showUser, setShowUser] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const { data: alertsData } = useAlertEvents({ acknowledged: false });
  const acknowledge = useAcknowledgeAlert();

  const unackCount = alertsData?.unacknowledgedCount || 0;
  const recentAlerts = (alertsData?.events || []).slice(0, 5);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(e.target as Node)) setShowUser(false);
      if (alertRef.current && !alertRef.current.contains(e.target as Node)) setShowAlerts(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
      {/* Search */}
      <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 w-80">
        <Search className="w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search by CNPJ or company name..."
          className="bg-transparent text-sm text-slate-900 placeholder:text-slate-400 outline-none w-full"
        />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {/* Alerts bell */}
        <div className="relative" ref={alertRef}>
          <button
            onClick={() => { setShowAlerts(!showAlerts); setShowUser(false); }}
            className="relative p-2 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <Bell className="w-5 h-5 text-slate-500" />
            {unackCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                {unackCount > 9 ? "9+" : unackCount}
              </span>
            )}
          </button>

          {showAlerts && (
            <div className="absolute right-0 top-12 w-80 bg-white rounded-xl border border-slate-200 shadow-lg z-50">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-sm font-medium text-slate-900">Alerts</h3>
                <button
                  onClick={() => { setShowAlerts(false); navigate("/alerts"); }}
                  className="text-xs text-blue-500 hover:text-blue-600 flex items-center gap-0.5"
                >
                  View all <ChevronRight className="w-3 h-3" />
                </button>
              </div>
              {recentAlerts.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-slate-400">
                  No pending alerts
                </div>
              ) : (
                <div className="max-h-72 overflow-y-auto divide-y divide-slate-50">
                  {recentAlerts.map((alert: Record<string, unknown>) => (
                    <div key={alert.id as number} className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50">
                      <span className={cn("w-2 h-2 rounded-full mt-1.5 flex-shrink-0",
                        alert.severity === "critical" ? "bg-red-500" : alert.severity === "warning" ? "bg-amber-500" : "bg-blue-500"
                      )} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900 truncate">{String(alert.title)}</p>
                        <p className="text-xs text-slate-400 truncate">
                          {String(alert.razao_social || alert.cnpj || "")}
                        </p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); acknowledge.mutate(alert.id as number); }}
                        className="p-1 rounded hover:bg-slate-100 flex-shrink-0"
                        title="Acknowledge"
                      >
                        <Check className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* User menu */}
        <div className="relative" ref={userRef}>
          <button
            onClick={() => { setShowUser(!showUser); setShowAlerts(false); }}
            className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <div className="w-7 h-7 bg-slate-200 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-slate-600" />
            </div>
            <span className="text-sm text-slate-700 hidden sm:block">{user?.name?.split(" ")[0]}</span>
          </button>

          {showUser && (
            <div className="absolute right-0 top-12 w-56 bg-white rounded-xl border border-slate-200 shadow-lg z-50">
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                <p className="text-xs text-slate-400 mt-0.5">{tenant?.name} <span className="capitalize">({tenant?.plan})</span></p>
              </div>
              <div className="py-1">
                <button
                  onClick={() => { setShowUser(false); navigate("/settings"); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <Settings className="w-4 h-4 text-slate-400" /> Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
